import { NavigateFunction } from 'react-router';
import { TFunction } from 'i18next';

import { ToAttachmentWithPath } from '../../api/utils';
import { AudioPlayer } from '../../hooks/useAudioPlayer';
import {
  AttachmentAudio,
  BalanceJson,
  I18nLocale,
  UiLocale,
  Uuid,
} from '../../models/Base';
// import { MediaFile, ResourceFile } from '../../models/FileStore';
import {
  GameCollectionGame,
  GameCollectionItem,
  GameDataWithVariant,
  GameJson,
  GameReply,
  GameStatistics,
  GameType,
  GameTypes,
} from '../../models/GameStore';
import {
  ChallengeRecordJson,
  GameRecord,
  GameRecordJson,
  TextEvaluationWord,
} from '../../models/RecordStore';
import { Profile } from '../../models/UserStore';
import { GameMode, GameParams } from '../../stores/GameControllerStore';
import { useTimer } from '../../stores/TimeStore';
import { StrokeCharacterData } from '../FileStore';

import { ThemeType } from './theme';

export type Utils = {
  t: TFunction;
  locale: UiLocale | undefined;
  userId: string;
  mode: GameMode;
  signal: AbortSignal;
};

// GameDisplayData
type PlayRecord = {
  score: number | undefined;
  durationInMs: number | undefined;
};

export type MemberReward = { puzzlePieces: number[]; classroomCredit: number };

export type OpponentsRecords = {
  profiles: Profile[];
  opponents: GameRecord[];
};

export type GameDisplayData = {
  classroomId: string;
  gameSetId: string;
  theme: ThemeType | undefined;
  isChallenge: boolean;
  oneTimeFee: boolean;
  gameBGIndex: number | undefined;
  context: {
    icon: string;
    title: string;
    color: string;
  };
  progress: {
    icon: string;
    title: string;
    completed: number | undefined;
    total: number | undefined;
  };
  body: {
    index: number | undefined;
    icon?: string;
    // subscript?: GraphicsV2SourceType;
    title: string;
    color: string;
    gameIconTag: string | null | undefined;
  };
  latestRecord?: PlayRecord;
  bestRecord?: PlayRecord;
  completedBefore: boolean;
  maximumReward: BalanceJson;
  maximumShowReward?: BalanceJson;
  fee: BalanceJson;
  statistics: { playCount: number; averageScore: number } | undefined;
  partIndex?: string | null;
  memberReward?: MemberReward;
  customReplies?: GameReply[];
  competition?: {
    chance: number;
    unlockedNext: boolean;
    gameSetLinkWith: { linkWithChallenge?: string; linkWithNext?: string };
  };
  vsMode: boolean;
  dummies: OpponentsRecords | undefined;
};

// GameControllerIntermediate
export type GameControllerIntermediateGameItem = GameCollectionItem & {
  gameCollectionId: Uuid;
  game: GameCollectionGame;
  fake?: boolean;
  category?: number;
};

export type GameControllerIntermediate = {
  gameItems: GameControllerIntermediateGameItem[];
  useMic: boolean;
  useSpeechRecognition: boolean;
  useCamera?: boolean;
  result?: never;
};

// GameControllerResult
export type GameInfo<
  G extends GameType = GameType,
  withI18n = true,
> = (withI18n extends false ? Omit<GameJson, 'i18nGameData'> : GameJson) & {
  hash: string;
  category?: number;
  fake?: boolean;
  gameStatistics: GameStatistics | null | undefined;
  gameType: G;
};

export type GameControllerI18n<G extends GameType> = GameDataWithVariant<
  GameTypes[G]['variant']
> & {
  locale: I18nLocale;
};

export type GameControllerInfo<G extends GameType> = GameInfo<G, false> & {
  gameData: GameControllerI18n<G>;
  translateGameData: GameControllerI18n<G>[];
};

// export type FileData<
//   F extends MediaFile | ResourceFile = MediaFile | ResourceFile,
// > = {
//   version?: keyof F['versions'];
//   fileType: F['fileType'];
//   fileId: string;
//   scope: string;
// };

export type FileUrls = {
  images: Record<Uuid, string | undefined>;
  audios: Record<Uuid, string | undefined>;
  videos: Record<Uuid, string | undefined>;
  resourceImages: Record<Uuid, string | undefined>;
  strokes: Record<Uuid, string | undefined>;
  ttsAudios: Record<Uuid, string | undefined>;
};

export type GameControllerResult<
  R extends object = Record<string, unknown>,
  G extends GameType = GameType,
> = {
  gameSequence: (
    | (Omit<GameControllerInfo<G>, 'timeLimit'> & { timeLimit: number })
    | undefined
  )[];
  defaultIndex: number;
  fileUrls: FileUrls;
  records: R;
};

// GameControllerRecordJson
export type GameControllerRecordJson = {
  gameRecord?: GameRecordJson;
  challengeRecord?: ChallengeRecordJson;
};

export type GamePlayParams<
  RecordJson extends GameControllerRecordJson = GameControllerRecordJson,
> = {
  fee: BalanceJson;
  record?: RecordJson;
  index?: number;
};

export type GamePlayResult<
  RecordJson extends GameControllerRecordJson = GameControllerRecordJson,
> = {
  record: RecordJson;
  durationInMs: number;
} & Pick<
  GameControllerStoreState,
  'scores' | 'submitAudio' | 'inAppEvaluations' | 'audios'
>;

// State
type Timer = ReturnType<typeof useTimer>;

type BaseState<
  RecordJson extends GameControllerRecordJson = GameControllerRecordJson,
> = {
  params: GameParams<GameMode>;
  gameMode?: GameMode;
  progress?: number;
  param?: unknown;
  displayData?: GameDisplayData;
  gameSequence?: GameControllerResult['gameSequence'];
  fileUrls?: GameControllerResult['fileUrls'];
  index?: number;
  error?: Error;
  initializeRecord: (
    gameParams: GamePlayParams<RecordJson>,
  ) => Promise<RecordJson>;
  finalizeRecord: (gameResult: GamePlayResult<RecordJson>) => Promise<{
    record: RecordJson;
    isBestInType?: boolean;
  }>;
  audioPlayer: AudioPlayer;
  strokeDatas: Record<string, StrokeCharacterData>;
  timer: Timer;
  useMic: boolean;
  useCamera: boolean;
  useSpeechRecognition: boolean;
  isLoading: boolean;
};

type StagePointCounter = {
  count: number;
  max: number;
  hiddenCount: number;
  hiddenMax?: number;
  flushed: boolean;
};

export enum EvaluationType {
  InApp = 'inApp',
  Improvement = 'improvement',
}

export type InAppEvaluations = {
  word: TextEvaluationWord[];
  type: EvaluationType;
};

type ScoreState<RecordJson> = {
  record: undefined | RecordJson;
  isBestInType: boolean;
  score: number;
  scores: Record<string, number[]>;
  stagePointCounter: StagePointCounter;
  submitAudio: boolean | undefined; // true: need to submit; false: no need submit; undefined: normal game
  inAppEvaluations: (InAppEvaluations | undefined)[];
  audios: (ToAttachmentWithPath<AttachmentAudio> | undefined)[][];
  isScoreKeeping: boolean;
};

export enum GamePlayState {
  Idle = 'idle',
  Start = 'start', // prepare game stage screen
  Run = 'run', // prepare game type screen
  Running = 'running', // running game
  Pause = 'pause', // pause game
  Resume = 'resume', // resume from game
  Score = 'score', // score game type screen and auto go WillEnd
  ScoreOnly = 'scoreOnly', // score game type screen
  WillEnd = 'willEnd', // end game type screen
  End = 'end', // end game stage screen
  Reset = 'reset', // reset all
}

export type RealGamePlayState = Exclude<GamePlayState, GamePlayState.ScoreOnly>;

type ScreenShotInfo = {
  index: number;
  paths: string[];
  shotTimeout: number;
};

export enum GameScreen {
  // GameTest = 'GameTest',
  Loading = 'Loading',
  ChallengeStart = 'ChallengeStart',
  GameStart = 'GameStart',
  GamePlay = 'GamePlay',
  GameEnd = 'GameEnd',
  GameResult = 'GameResult',
  GameAnalysis = 'GameAnalysis',
  PostGameAnalysis = 'PostGameAnalysis',
  JoyBall = 'JoyBall',
  JoyPet = 'JoyPet',
  WeeklyLeaderboard = 'WeeklyLeaderboard',
  ChallengeEnd = 'ChallengeEnd',
}

type FlowState = {
  gameScreen: GameScreen;
  gamePlayState: RealGamePlayState;
  screenShotInfo: ScreenShotInfo;
  nextGameId: string | undefined;
  hasNext: boolean;
  hasSlideshow: boolean;
  evaluationForImprovement: InAppEvaluations | null | undefined; // InAppEvaluations TextEvaluationWord[] = improvement, null = full, undefined = no
  improvementPlayCount: number;
  fullPlayCount: number;
};

export type GameControllerStoreState<
  RecordJson extends GameControllerRecordJson = GameControllerRecordJson,
> = BaseState<RecordJson> & ScoreState<RecordJson> & FlowState;

// Action
export type ExtractDisplayDataFn<
  Params,
  Intermediate extends GameControllerIntermediate,
  Result extends GameControllerResult,
> = (
  utils: Utils,
  params: Readonly<Params>,
  intermediate?: Intermediate,
  result?: Result,
) => GameDisplayData;

export type PrepareIntermediateFn<
  Params,
  Intermediate extends GameControllerIntermediate,
> = (
  utils: Utils,
  params: Readonly<Params>,
) => Promise<
  Partial<Params & { intermediate?: never }> | { intermediate: Intermediate }
>;

export type FetchDataFn<
  Params,
  Data,
  Intermediate extends GameControllerIntermediate,
> = (
  utils: Utils,
  params: Readonly<Params>,
  intermediate: Intermediate,
) => Promise<({ result?: Data } | Intermediate) & { progress: number }>;

export type RefineResultFn<
  Params,
  Data,
  Intermediate extends GameControllerIntermediate,
  Result extends GameControllerResult,
> = (
  utils: Utils,
  params: Readonly<Params>,
  intermediate: Intermediate,
  result: Data,
) => Result;

export type InitializeRecordFn<
  Params,
  Intermediate extends GameControllerIntermediate,
  Result extends GameControllerResult,
  RecordJson extends GameControllerRecordJson,
> = (
  utils: Utils,
  params: Readonly<Params>,
  intermediate: Intermediate,
  result: Result,
  gameParams: GamePlayParams<RecordJson>,
) => Promise<RecordJson>;

export type FinalizeRecordFn<
  Params,
  Intermediate extends GameControllerIntermediate,
  Result extends GameControllerResult,
  RecordJson extends GameControllerRecordJson,
> = (
  utils: Utils,
  params: Readonly<Params>,
  intermediate: Intermediate,
  result: Result,
  gameResult: GamePlayResult<RecordJson>,
) => Promise<{
  record: RecordJson;
  isBestInType?: boolean;
  increaseMissionProgress?: boolean;
}>;

type BaseAction = {
  setup: <
    Params extends GameParams<GameMode>,
    Data,
    Intermediate extends GameControllerIntermediate,
    Result extends GameControllerResult,
    RecordJson extends GameControllerRecordJson,
  >(
    utils: Utils,
    params: Params,
    extractDisplayData: ExtractDisplayDataFn<Params, Intermediate, Result>,
    prepareIntermediate: PrepareIntermediateFn<Params, Intermediate>,
    fetchData: FetchDataFn<Params, Data, Intermediate>,
    refineResult: RefineResultFn<Params, Data, Intermediate, Result>,
    initializeRecord: InitializeRecordFn<
      Params,
      Intermediate,
      Result,
      RecordJson
    >,
    finalizeRecord: FinalizeRecordFn<Params, Intermediate, Result, RecordJson>,
    audioPlayer: AudioPlayer,
    timer: Timer,
  ) => Promise<void>;
  cleanup: () => void;
};

type ScoreAction = {
  startScoreKeeping: (fee: BalanceJson) => Promise<void>;
  startStage: (maxPoint: number, hiddenMax?: number) => void;
  addScore: (increment: number) => number;
  addStagePoint: (count: number, hiddenCount?: number) => void;
  setInAppEvaluation: (evaluation: InAppEvaluations) => void;
  flushScore: (category?: string) => void;
  endScoreKeeping: (durationInMs: number) => Promise<void>;
  getScoreDisplay: () => [scoreDisplayText: string, score: number];
  getScoreInfo: () => { count: number; total: number };
  setAudio: (
    audio: ToAttachmentWithPath<AttachmentAudio> | undefined,
    index?: number,
  ) => void;
  setSubmitAudio: (submitAudio: boolean | undefined) => void;
};

type FlowAction = {
  onGameLoaded: () => Promise<void>;
  onGameStart: () => Promise<void>;
  //   | ((
  //       startRewardAnimation: (
  //         balance: BalanceJson,
  //         jumpPage: () => void,
  //       ) => void,
  //     ) => void)
  //   | BalanceJson
  // >;
  onGameStageEnd: (durationInMs: number) => Promise<void>;
  onNextGame: (navigate: NavigateFunction) => void;
  onLeaveGame: (navigate: NavigateFunction) => void;
  onAnalysis: () => void;
  onRetry: (navigate: NavigateFunction) => void;
  onNext: (navigate: NavigateFunction) => void;
  onSlideshow: () => void;
  onPractice: (navigate: NavigateFunction) => void;
  onSendImage: () => void;
  setGamePlayState: (state: GamePlayState, delay?: number) => Promise<void>;
  initializeNewLap: (timeLimit?: number) => void;
  playAudio: (id?: string) => void;
  stopAudio: (id?: string) => void;
  getAudio: (id?: string) => string | undefined;
};

export type GameController = {
  setGamePlayState: (state: GamePlayState, delay?: number) => Promise<void>;
  initializeNewLap: (timeLimit?: number) => void;
  startStage: (maxPoint: number, hiddenMax?: number) => void;
  addScore: (increment: number) => number;
  addStagePoint: (count: number, hiddenCount?: number) => void;
  setInAppEvaluation: (evaluation: InAppEvaluations) => void;
  playAudio: (id?: string) => void;
  stopAudio: (id?: string) => void;
  getAudio: (id?: string) => string | undefined;
};

export type GameControllerStoreAction = BaseAction & ScoreAction & FlowAction;
